import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { sendFeedback } from "@/services/authService";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Feedback composer. Previously a popover hanging off a permanent header
 * button; it now opens from the sidebar's user menu, which is where the other
 * account-level actions (profile, theme, sign out) already live. Extracted so
 * the send logic has one home rather than being inlined at the trigger.
 */
export function FeedbackDialog({
  open,
  onOpenChange,
}: FeedbackDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async (): Promise<void> => {
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await sendFeedback(trimmed);
      toast.success(t("header.feedbackSuccess"));
      setMessage("");
      onOpenChange(false);
    } catch {
      toast.error(t("header.feedbackError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("header.feedback")}</DialogTitle>
          <DialogDescription>
            {t(
              "header.feedbackDescription",
              "Contanos qué podemos mejorar. Leemos todo.",
            )}
          </DialogDescription>
        </DialogHeader>

        <textarea
          className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder={t("header.feedbackPlaceholder")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={2000}
          disabled={sending}
          autoFocus
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="fx-btn fx-btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            {t("common.cancel", "Cancelar")}
          </button>
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!message.trim() || sending}
            className="fx-btn fx-btn-accent"
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("header.feedbackSending")}
              </>
            ) : (
              t("header.feedbackSend")
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
